import { useEffect, useMemo, useRef, useState } from 'react';
import { createRoute, useParams } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { ROTAS, type Palavra } from '@remoto/shared';
import { rotaPainel } from './painel';
import { chamar } from '../api';
import { assinarFluxo, socketDoPainel } from '../socket/painel';
import {Cartao} from '../componentes/layout';
import { Resumos } from '../componentes/resumos';
import { duracaoLegivel, type GravacaoNaLista } from './audio';
import { Tela } from '../workspace/Tela';

interface Segmento {
  seq: number;
  inicioMs: number;
  fimMs: number;
  texto: string;
  palavras: Palavra[];
}

/** Índice da palavra que está tocando agora, por busca binária. */
function palavraNoTempo(palavras: Palavra[], ms: number): number {
  let baixo = 0;
  let alto = palavras.length - 1;
  let achou = -1;
  while (baixo <= alto) {
    const meio = (baixo + alto) >> 1;
    const palavra = palavras[meio] as Palavra;
    if (ms < palavra.i) {
      alto = meio - 1;
    } else if (ms > palavra.f) {
      baixo = meio + 1;
    } else {
      return meio;
    }
    if (palavra.i <= ms) achou = meio;
  }
  return achou;
}

function Detalhe(): React.JSX.Element {
  // `_painel` é a rota de layout: o caminho continua `/audio/$id`, mas o id
  // interno da rota ganhou o prefixo dela, e é o id que o `from` pede.
  const { id } = useParams({ from: '/_painel/audio/$id' });
  const player = useRef<HTMLAudioElement>(null);
  const [segmentos, setSegmentos] = useState<Segmento[]>([]);
  const [agoraMs, setAgoraMs] = useState(0);

  const { data: gravacao } = useQuery({
    queryKey: ['gravacao', id],
    queryFn: () => chamar<GravacaoNaLista>(`${ROTAS.gravacoes}/${id}`),
    refetchInterval: (consulta) =>
      consulta.state.data?.estado === 'CONCLUIDA' || consulta.state.data?.estado === 'ERRO'
        ? false
        : 3_000,
  });

  const { data: audio } = useQuery({
    queryKey: ['gravacao-audio', id],
    queryFn: () => chamar<{ url: string }>(`${ROTAS.gravacoes}/${id}/reproducao`),
    retry: false,
  });

  /**
   * Carga inicial pelo REST e só então a assinatura do fluxo com o cursor do
   * que já chegou: o replay entrega exatamente o que falta, sem repetir o que
   * a tela já mostra.
   */
  useEffect(() => {
    let vivo = true;
    const socket = socketDoPainel();

    let cursor = 0;
    let cancelarAssinatura: (() => void) | null = null;

    const aoChegar = (segmento: Segmento & { fluxo?: string }): void => {
      if (segmento.seq > cursor) cursor = segmento.seq;
      setSegmentos((atuais) =>
        atuais.some((s) => s.seq === segmento.seq)
          ? atuais
          : [...atuais, segmento].sort((a, b) => a.seq - b.seq),
      );
    };

    void chamar<{ itens: Segmento[] }>(`${ROTAS.gravacoes}/${id}/segmentos?limite=500`).then(
      (pagina) => {
        if (!vivo) return;
        setSegmentos(pagina.itens);
        cursor = pagina.itens.at(-1)?.seq ?? 0;
        socket.on('transcricao.segmento', aoChegar);
        cancelarAssinatura = assinarFluxo('gravacao:' + id, () => cursor);
      },
    );

    return () => {
      vivo = false;
      socket.off('transcricao.segmento', aoChegar);
      cancelarAssinatura?.();
    };
  }, [id]);

  // O `timeupdate` do navegador dispara a cada 250 ms; para o destaque não
  // ficar aos solavancos, o tempo é lido a cada quadro enquanto toca.
  useEffect(() => {
    let quadro = 0;
    const acompanhar = (): void => {
      const elemento = player.current;
      if (elemento && !elemento.paused) setAgoraMs(Math.round(elemento.currentTime * 1000));
      quadro = requestAnimationFrame(acompanhar);
    };
    quadro = requestAnimationFrame(acompanhar);
    return () => cancelAnimationFrame(quadro);
  }, []);

  const palavras = useMemo(() => segmentos.flatMap((s) => s.palavras), [segmentos]);
  const atual = useMemo(() => palavraNoTempo(palavras, agoraMs), [palavras, agoraMs]);
  const palavraAtual = atual >= 0 ? palavras[atual] : undefined;

  function irPara(ms: number): void {
    const elemento = player.current;
    if (!elemento) return;
    elemento.currentTime = ms / 1000;
    setAgoraMs(ms);
    void elemento.play();
  }

  return (
    <Tela>
      <Cartao titulo={gravacao?.titulo ?? 'Gravação'}>
        <p className="text-xs text-zinc-500">
          {duracaoLegivel(gravacao?.duracaoMs ?? null)} · {segmentos.length} trecho(s) de texto
          {gravacao?.estado === 'TRANSCREVENDO' ? ' · transcrevendo ao vivo' : ''}
          {gravacao?.estado === 'PROCESSANDO' ? ' · preparando o áudio' : ''}
        </p>
        {audio?.url ? (
          <audio ref={player} src={audio.url} controls preload="metadata" className="mt-3 w-full" />
        ) : (
          <p className="mt-3 text-sm text-zinc-500">O áudio ainda não está pronto para tocar.</p>
        )}
      </Cartao>

      <Cartao titulo="Transcrição">
        {segmentos.length === 0 ? (
          <p className="text-sm text-zinc-500">
            Nada transcrito ainda. O texto aparece aqui conforme fica pronto.
          </p>
        ) : (
          <div className="space-y-3 leading-relaxed">
            {segmentos.map((segmento) => (
              <p key={segmento.seq} className="text-zinc-300">
                {segmento.palavras.map((palavra, i) => {
                  const tocando =
                    palavraAtual !== undefined &&
                    palavra.i === palavraAtual.i &&
                    palavra.p === palavraAtual.p;
                  return (
                    <span
                      key={segmento.seq + '-' + i}
                      onClick={() => irPara(palavra.i)}
                      className={
                        'cursor-pointer rounded px-0.5 transition ' +
                        (tocando ? 'bg-emerald-500/20 text-emerald-200' : 'hover:bg-zinc-800')
                      }
                    >
                      {palavra.p}{' '}
                    </span>
                  );
                })}
              </p>
            ))}
          </div>
        )}
      </Cartao>

      <Resumos gravacaoId={id} />
    </Tela>
  );
}

export const rotaAudioDetalhe = createRoute({
  getParentRoute: () => rotaPainel,
  path: '/audio/$id',
  component: Detalhe,
});
